import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  TextField,
  Tooltip,
  Chip,
  Checkbox,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FilterListIcon from '@mui/icons-material/FilterList';
import PageLayout from '../../components/layout/PageLayout';
import ImportBusinessDialog from '../../components/ImportBusinesses/ImportBusinessDialog';
import ImportBusinessFilters from '../../components/ImportBusinesses/ImportBusinessFilters';
import EditImportBusinessDialog from '../../components/ImportBusinesses/EditImportBusinessDialog';
import ChangeImportBusinessStatusDialog from '../../components/ImportBusinesses/ChangeImportBusinessStatusDialog';
import AssignImportBusinessDialog from '../../components/ImportBusinesses/AssignImportBusinessDialog';
import { IMPORT_BUSINESSES_VIEW_PATH } from '../../navigations/path';
import Can from '../../components/permissions/Can';
import { useAuth } from '../../hooks/useAuth';
import api from '../../api/config';
import { IMPORT_BUSINESSES } from '../../api/endpoints';
import { extractList, extractPagination } from '../../utils/response';
import { toast } from 'react-toastify';
import { SweetAlert } from '../../utils/sweetAlert';
import { getLeadsStatus } from '../../store/slices/leadsSlice';
import {
  getBusinessCategories,
  getStates,
  getUnitedStatesId,
} from '../../store/slices/lookupSlice';
import {
  buildImportBusinessListParams,
  resolveImportBusinessId,
  getImportBusinessName,
  getImportBusinessCategory,
  getImportBusinessStatus,
  getImportBusinessState,
  getImportBusinessAssignee,
  formatImportBusinessDate,
  ellipsizeImportBusiness,
  displayImportBusinessValue,
  IMPORT_BUSINESS_STATUS_MODEL,
} from './importBusinessApiHelpers';
import {
  IMPORT_BUSINESS_PERMISSIONS,
  IMPORT_BUSINESS_VIEW_ACCESS,
} from './importBusinessPermissions';

const ImportBusinessList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { hasPermission } = useAuth();
  const canAssign = hasPermission(IMPORT_BUSINESS_PERMISSIONS.ASSIGN);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, per_page: 15, total: 0 });
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status_id: '',
    business_category_id: '',
    state_id: '',
  });
  const [sort, setSort] = useState({
    sort_by: 'created_at',
    sort_direction: 'desc',
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deletingId, setDeletingId] = useState(null);
  const [statusOptions, setStatusOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [statusFilterLoading, setStatusFilterLoading] = useState(false);
  const [categoryFilterLoading, setCategoryFilterLoading] = useState(false);
  const [stateFilterLoading, setStateFilterLoading] = useState(false);
  const statusInFlightRef = useRef(null);
  const categoryInFlightRef = useRef(null);
  const stateInFlightRef = useRef(null);

  const ensureStatusFilterOptions = useCallback(async () => {
    if (statusOptions.length > 0) return statusOptions;
    if (statusInFlightRef.current) return statusInFlightRef.current;
    setStatusFilterLoading(true);
    const request = (async () => {
      try {
        const data = await dispatch(
          getLeadsStatus({ 'conditions[model]': IMPORT_BUSINESS_STATUS_MODEL })
        ).unwrap();
        const list = extractList(data);
        setStatusOptions(list);
        return list;
      } catch {
        toast.error('Failed to load status options');
        return [];
      } finally {
        setStatusFilterLoading(false);
        statusInFlightRef.current = null;
      }
    })();
    statusInFlightRef.current = request;
    return request;
  }, [dispatch, statusOptions]);

  const ensureCategoryFilterOptions = useCallback(async () => {
    if (categoryOptions.length > 0) return categoryOptions;
    if (categoryInFlightRef.current) return categoryInFlightRef.current;
    setCategoryFilterLoading(true);
    const request = (async () => {
      try {
        const data = await dispatch(getBusinessCategories()).unwrap();
        const list = extractList(data);
        setCategoryOptions(list);
        return list;
      } catch {
        toast.error('Failed to load category options');
        return [];
      } finally {
        setCategoryFilterLoading(false);
        categoryInFlightRef.current = null;
      }
    })();
    categoryInFlightRef.current = request;
    return request;
  }, [dispatch, categoryOptions]);

  const ensureStateFilterOptions = useCallback(async () => {
    if (stateOptions.length > 0) return stateOptions;
    if (stateInFlightRef.current) return stateInFlightRef.current;
    setStateFilterLoading(true);
    const request = (async () => {
      try {
        const usId = await dispatch(getUnitedStatesId()).unwrap();
        if (!usId) {
          setStateOptions([]);
          return [];
        }
        const states = await dispatch(getStates(usId)).unwrap();
        const list = Array.isArray(states) ? states : extractList(states);
        setStateOptions(list);
        return list;
      } catch {
        toast.error('Failed to load state options');
        return [];
      } finally {
        setStateFilterLoading(false);
        stateInFlightRef.current = null;
      }
    })();
    stateInFlightRef.current = request;
    return request;
  }, [dispatch, stateOptions]);

  const ensureEditDialogOptions = useCallback(async () => {
    await Promise.all([
      ensureStatusFilterOptions(),
      ensureCategoryFilterOptions(),
      ensureStateFilterOptions(),
    ]);
  }, [ensureStatusFilterOptions, ensureCategoryFilterOptions, ensureStateFilterOptions]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(IMPORT_BUSINESSES.LIST, {
        params: buildImportBusinessListParams({
          page: pagination.page,
          per_page: pagination.per_page,
          search,
          sort_by: sort.sort_by,
          sort_direction: sort.sort_direction,
          filters,
        }),
      });
      setRows(extractList(data));
      const pag = extractPagination(data);
      setPagination((prev) => ({
        ...prev,
        total: pag.total ?? 0,
        page: pag.page ?? prev.page,
      }));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load data');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.per_page, search, filters, sort]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleSortChange = (nextSort) => {
    setSort(nextSort);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      status_id: '',
      business_category_id: '',
      state_id: '',
    });
    setSort({ sort_by: 'created_at', sort_direction: 'desc' });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const getActiveFiltersCount = () =>
    Object.values(filters).filter((v) => v !== '' && v != null).length;

  const handleChangePage = (_, newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage + 1 }));
  };

  const handleChangeRowsPerPage = (event) => {
    const perPage = parseInt(event.target.value, 10) || 15;
    setPagination((prev) => ({ ...prev, page: 1, per_page: perPage }));
  };

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    setSearch(searchInput.trim());
  };

  const handleOpenMenu = (event, row) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setMenuRow(row);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const goView = (row) => {
    const id = resolveImportBusinessId(row ?? menuRow);
    handleCloseMenu();
    setMenuRow(null);
    if (id) navigate(`${IMPORT_BUSINESSES_VIEW_PATH}/${id}`);
  };

  const handleOpenEdit = async () => {
    handleCloseMenu();
    await ensureEditDialogOptions();
    setEditDialogOpen(true);
  };

  const handleOpenStatus = () => {
    handleCloseMenu();
    setStatusDialogOpen(true);
  };

  const handleDialogClose = () => {
    setEditDialogOpen(false);
    setStatusDialogOpen(false);
    setMenuRow(null);
  };

  const handleDelete = async () => {
    const row = menuRow;
    const id = resolveImportBusinessId(row);
    if (!id) return;

    handleCloseMenu();
    const result = await SweetAlert.confirm({
      title: 'Delete data record?',
      text: `"${getImportBusinessName(row)}" will be deleted. This action cannot be undone.`,
      confirmButtonText: 'Delete',
      icon: 'warning',
    });
    if (!result.isConfirmed) {
      setMenuRow(null);
      return;
    }

    setDeletingId(id);
    try {
      const { data } = await api.delete(IMPORT_BUSINESSES.BY_ID(id));
      toast.success(data?.message || 'Import business deleted successfully');
      setSelectedIds((prev) => prev.filter((item) => item !== id));
      setMenuRow(null);
      fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete data record');
    } finally {
      setDeletingId(null);
    }
  };

  const handleImportClose = (success) => {
    setImportDialogOpen(false);
    if (success) fetchRows();
  };

  const pageRowIds = rows
    .map((row) => resolveImportBusinessId(row))
    .filter(Boolean);

  const isSelected = (id) => selectedIds.includes(id);

  const handleToggleRow = (event, id) => {
    event.stopPropagation();
    if (!id) return;
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleToggleAllPage = (event) => {
    event.stopPropagation();
    const allPageSelected =
      pageRowIds.length > 0 && pageRowIds.every((id) => selectedIds.includes(id));
    if (allPageSelected) {
      setSelectedIds((prev) => prev.filter((id) => !pageRowIds.includes(id)));
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...pageRowIds])]);
    }
  };

  const handleOpenAssign = () => {
    if (!selectedIds.length) {
      toast.error('Please select at least one data record');
      return;
    }
    setAssignDialogOpen(true);
  };

  const handleAssignClose = () => {
    setAssignDialogOpen(false);
  };

  const handleAssigned = () => {
    setSelectedIds([]);
    fetchRows();
  };

  const allPageSelected =
    pageRowIds.length > 0 && pageRowIds.every((id) => selectedIds.includes(id));
  const somePageSelected =
    pageRowIds.some((id) => selectedIds.includes(id)) && !allPageSelected;

  const colCount = canAssign ? 11 : 10;

  return (
    <PageLayout>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box
          sx={{
            mb: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 600 }}>
            Data ({pagination.total.toLocaleString()})
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Can permission={IMPORT_BUSINESS_PERMISSIONS.ASSIGN}>
              <Button
                variant="outlined"
                startIcon={<PersonAddIcon />}
                onClick={handleOpenAssign}
                disabled={!selectedIds.length}
              >
                Assign Assignee{selectedIds.length ? ` (${selectedIds.length})` : ''}
              </Button>
            </Can>
            <Can permission={IMPORT_BUSINESS_PERMISSIONS.CREATE}>
              <Button
                variant="contained"
                startIcon={<CloudUploadIcon />}
                onClick={() => setImportDialogOpen(true)}
              >
                Import Data
              </Button>
            </Can>
          </Box>
        </Box>

        <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search data..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            sx={{ minWidth: 260 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
          />
          <Button variant="outlined" onClick={handleSearch}>
            Search
          </Button>
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={() => setShowFilters(!showFilters)}
            sx={{
              position: 'relative',
              ...(getActiveFiltersCount() > 0 && {
                borderColor: 'primary.main',
                backgroundColor: 'primary.light',
              }),
            }}
          >
            Filters
            {getActiveFiltersCount() > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  backgroundColor: 'primary.main',
                  color: 'white',
                  borderRadius: '50%',
                  width: 20,
                  height: 20,
                  fontSize: '0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {getActiveFiltersCount()}
              </Box>
            )}
          </Button>
          <Button
            variant="text"
            onClick={() => {
              setSearchInput('');
              setSearch('');
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
          >
            Clear
          </Button>
        </Box>

        <ImportBusinessFilters
          showFilters={showFilters}
          filters={filters}
          sort={sort}
          statusOptions={statusOptions}
          categoryOptions={categoryOptions}
          stateOptions={stateOptions}
          statusFilterLoading={statusFilterLoading}
          categoryFilterLoading={categoryFilterLoading}
          stateFilterLoading={stateFilterLoading}
          onStatusOpen={ensureStatusFilterOptions}
          onCategoryOpen={ensureCategoryFilterOptions}
          onStateOpen={ensureStateFilterOptions}
          onFilterChange={handleFilterChange}
          onSortChange={handleSortChange}
          onClearFilters={clearFilters}
        />

        <Card>
          <CardContent sx={{ p: 0 }}>
            <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'auto' }}>
              <Table size="small" stickyHeader aria-label="data table">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.100' }}>
                    {canAssign && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={somePageSelected}
                          checked={allPageSelected}
                          onChange={handleToggleAllPage}
                          onClick={(e) => e.stopPropagation()}
                          disabled={loading || pageRowIds.length === 0}
                          inputProps={{ 'aria-label': 'Select all on page' }}
                        />
                      </TableCell>
                    )}
                    <TableCell sx={{ fontWeight: 600 }}>Business Category</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 220 }}>Address</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>State</TableCell>
                    <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Assignee</TableCell>
                    <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Assigned At</TableCell>
                    <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Created At</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 72, textAlign: 'center' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={colCount} align="center" sx={{ py: 4 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={colCount} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No Data found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => {
                      const rowId = resolveImportBusinessId(row);
                      const checked = isSelected(rowId);
                      return (
                        <TableRow
                          key={rowId}
                          hover
                          selected={canAssign && checked}
                          onClick={() => goView(row)}
                          sx={{ cursor: rowId ? 'pointer' : 'default' }}
                        >
                          {canAssign && (
                            <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                checked={checked}
                                onChange={(e) => handleToggleRow(e, rowId)}
                                onClick={(e) => e.stopPropagation()}
                                inputProps={{ 'aria-label': `Select ${getImportBusinessName(row)}` }}
                              />
                            </TableCell>
                          )}
                          <TableCell sx={{ maxWidth: 180 }}>
                            <Tooltip title={getImportBusinessCategory(row)}>
                              <Typography variant="body2" noWrap>
                                {getImportBusinessCategory(row)}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 220 }}>
                            <Tooltip title={getImportBusinessName(row)}>
                              <Typography variant="body2" noWrap>
                                {getImportBusinessName(row)}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {displayImportBusinessValue(row.phone)}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 280 }}>
                            <Tooltip title={row.address || ''}>
                              <Typography variant="body2" noWrap>
                                {ellipsizeImportBusiness(row.address, 48)}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {getImportBusinessState(row)}
                          </TableCell>
                          <TableCell>
                            <Chip label={getImportBusinessStatus(row)} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            <Tooltip title={getImportBusinessAssignee(row)}>
                              <Typography variant="body2" noWrap>
                                {getImportBusinessAssignee(row)}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {formatImportBusinessDate(row.assigned_at)}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {formatImportBusinessDate(row.created_at)}
                          </TableCell>
                          <TableCell onClick={(e) => e.stopPropagation()} sx={{ textAlign: 'center' }}>
                            <IconButton
                              size="small"
                              onClick={(e) => handleOpenMenu(e, row)}
                              disabled={deletingId === rowId}
                              aria-label="Actions"
                            >
                              {deletingId === rowId ? <CircularProgress size={20} /> : <MoreVertIcon />}
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {!loading && (
              <TablePagination
                component="div"
                count={pagination.total}
                page={pagination.page - 1}
                onPageChange={handleChangePage}
                rowsPerPage={pagination.per_page}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[15, 25, 50, 100]}
                labelRowsPerPage="Rows per page"
              />
            )}
          </CardContent>
        </Card>
      </Container>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Can permissions={IMPORT_BUSINESS_VIEW_ACCESS}>
          <MenuItem onClick={() => goView()}>
            <ListItemIcon>
              <VisibilityIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>View</ListItemText>
          </MenuItem>
        </Can>
        <Can permission={IMPORT_BUSINESS_PERMISSIONS.EDIT}>
          <MenuItem onClick={handleOpenEdit}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        </Can>
        <Can permission={IMPORT_BUSINESS_PERMISSIONS.STATUS}>
          <MenuItem onClick={handleOpenStatus}>
            <ListItemIcon>
              <SwapHorizIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Change Status</ListItemText>
          </MenuItem>
        </Can>
        <Can permission={IMPORT_BUSINESS_PERMISSIONS.DELETE}>
          <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
            <ListItemIcon>
              <DeleteOutlineIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        </Can>
      </Menu>

      <ImportBusinessDialog open={importDialogOpen} onClose={handleImportClose} />

      <EditImportBusinessDialog
        open={editDialogOpen}
        business={menuRow}
        statusOptions={statusOptions}
        stateOptions={stateOptions}
        categoryOptions={categoryOptions}
        onClose={handleDialogClose}
        onSaved={fetchRows}
      />

      <ChangeImportBusinessStatusDialog
        open={statusDialogOpen}
        business={menuRow}
        onClose={handleDialogClose}
        onSaved={fetchRows}
      />

      <AssignImportBusinessDialog
        open={assignDialogOpen}
        selectedBusinessIds={selectedIds}
        onClose={handleAssignClose}
        onAssigned={handleAssigned}
      />
    </PageLayout>
  );
};

export default ImportBusinessList;
